'use client';

import { useState } from 'react';
import { Check, Copy, Mail, Share2 } from 'lucide-react';
import {
  FacebookIcon,
  LinkedInIcon,
  TwitterIcon,
  WhatsAppIcon,
} from './BrandIcons';

interface SocialShareProps {
  url: string;
  title: string;
  description?: string;
  hashtags?: string[];
  className?: string;
}

export default function SocialShare({
  url,
  title,
  description = '',
  hashtags = ['C2E', 'Education', 'Togo'],
  className = ''
}: SocialShareProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [showCopiedNotification, setShowCopiedNotification] = useState(false);

  // Encoder les paramètres pour les URLs
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);
  const encodedDescription = encodeURIComponent(description);
  const encodedHashtags = encodeURIComponent(hashtags.join(','));

  // URLs de partage
  const shareUrls = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    whatsapp: `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}&hashtags=${encodedHashtags}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    email: `mailto:?subject=${encodedTitle}&body=${encodedDescription}%0A%0A${encodedUrl}`
  };

  const handleShare = (platform: keyof typeof shareUrls) => {
    const shareUrl = shareUrls[platform];
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setShowCopiedNotification(true);
      // Masquer la notification après 3 secondes
      setTimeout(() => setShowCopiedNotification(false), 3000);
    } catch (err) {
      console.error('Erreur lors de la copie:', err);
    }
  };

  const shareButtons = [
    {
      name: 'Facebook',
      icon: <FacebookIcon className="h-5 w-5" />,
      color: 'hover:bg-blue-600',
      bgColor: 'bg-blue-500',
      action: () => handleShare('facebook')
    },
    {
      name: 'WhatsApp',
      icon: <WhatsAppIcon className="h-5 w-5" />,
      color: 'hover:bg-green-600',
      bgColor: 'bg-green-500',
      action: () => handleShare('whatsapp')
    },
    {
      name: 'Twitter',
      icon: <TwitterIcon className="h-5 w-5" />,
      color: 'hover:bg-blue-400',
      bgColor: 'bg-blue-400',
      action: () => handleShare('twitter')
    },
    {
      name: 'LinkedIn',
      icon: <LinkedInIcon className="h-5 w-5" />,
      color: 'hover:bg-blue-700',
      bgColor: 'bg-blue-600',
      action: () => handleShare('linkedin')
    },
    {
      name: 'Email',
      icon: <Mail className="h-5 w-5" aria-hidden="true" />,
      color: 'hover:bg-gray-600',
      bgColor: 'bg-gray-500',
      action: () => handleShare('email')
    }
  ];

  return (
    <div className={`flex flex-col space-y-3 ${className}`}>
      {/* Bouton principal pour mobile */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="md:hidden flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 font-medium transition-colors"
      >
        <Share2 className="h-5 w-5" aria-hidden="true" />
        Partager
      </button>

      {/* Grille de boutons de partage */}
      <div className={`flex flex-wrap gap-2 ${isOpen ? 'block' : 'hidden md:flex'}`}>
        {shareButtons.map((button) => (
          <button
            key={button.name}
            onClick={button.action}
            className={`flex items-center gap-2 px-3 py-2 text-white rounded-lg transition-colors ${button.bgColor} ${button.color} text-sm font-medium`}
            title={`Partager sur ${button.name}`}
          >
            {button.icon}
            <span className="hidden sm:inline">{button.name}</span>
          </button>
        ))}
      </div>

      {/* Bouton pour copier le lien */}
      <div className="relative">
        <button
          onClick={handleCopyLink}
          className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 font-medium transition-colors text-sm"
          title="Copier le lien"
        >
          <Copy className="h-4 w-4" aria-hidden="true" />
          <span className="hidden sm:inline">Copier le lien</span>
        </button>

        {/* Notification de succès */}
        {showCopiedNotification && (
          <div className="absolute bottom-full left-0 mb-2 px-3 py-2 bg-green-500 text-white text-sm rounded-lg shadow-lg z-10 whitespace-nowrap">
            <div className="flex items-center gap-2">
              <Check className="h-4 w-4" aria-hidden="true" />
              Lien copié !
            </div>
          </div>
        )}
      </div>
    </div>
  );
}